import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import momentTZ from 'moment-timezone';
import { REPEATS, MAX_MONTH_DAY_OPTION, normalizeTrigger, isTriggerValid, scheduleToCron, ensureScheduleFields, defaultScheduleForDuration, nextRun, formatTime, parseTime, ordinalDay } from '@/utils/aggregationTrigger';
const I18N_BASE = 'DigitalTwin.DigitalTwin.states.timeseries-aggregate.trigger';
// index = JS getDay() / cron weekday numbering
const WEEKDAY_KEYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MINUTE_STEP = 5;
const pad2 = n => `${n}`.padStart(2, '0');
const escapeHtml = value => `${value === undefined || value === null ? '' : value}`.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** "Trigger" section of a timeseries-aggregate state; edits `timeAggregation.trigger` (see utils/aggregationTrigger.js). */
export default {
  name: 'CoreDtwinStateTimeseriesAggregateTrigger',
  props: {
    // raw trigger object of the state (may be missing on older states)
    value: {
      type: Object,
      required: false,
      default: null
    },
    // 'edit', 'create' or 'readonly'
    mode: {
      type: String,
      required: false,
      default: () => 'edit'
    },
    templateContext: {
      type: Boolean,
      required: false,
      default: false
    },
    templateTitle: {
      type: String,
      required: false,
      default: ''
    },
    // number of twins using the template; negative while unknown
    linkedTwinCount: {
      type: Number,
      required: false,
      default: -1
    },
    // timezone of the aggregation itself, used as default for a new schedule
    defaultTimeZone: {
      type: String,
      required: false,
      default: null
    },
    // aggregation duration (seconds or `now-1d/d` style), used to pick a fitting default repeat type
    duration: {
      type: [Number, String],
      required: false,
      default: null
    }
  },
  data() {
    return {
      trigger: normalizeTrigger(this.value),
      repeats: REPEATS,
      requiredRule: [v => !!v || this.$i18n_t('Common.fieldRequired')],
      timeRules: [v => !!parseTime(v) || this.$i18n_t('Common.fieldRequired')]
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'language']),
    readonly() {
      return this.mode === 'readonly';
    },
    schedule() {
      return this.trigger.schedule;
    },
    isHourly() {
      return !!this.schedule && this.schedule.repeat === 'hourly';
    },
    isWeekly() {
      return !!this.schedule && this.schedule.repeat === 'weekly';
    },
    isMonthly() {
      return !!this.schedule && this.schedule.repeat === 'monthly';
    },
    // exposed to the edit-dialog's validateForm()
    formIsValid() {
      return isTriggerValid(this.trigger);
    },
    cron() {
      return scheduleToCron(this.schedule);
    },
    timeString() {
      if (!this.schedule) {
        return '';
      }
      return formatTime(this.schedule.hour || 0, this.schedule.minute || 0);
    },
    minuteOptions() {
      const options = [];
      for (let minute = 0; minute < 60; minute += MINUTE_STEP) {
        options.push({
          value: minute,
          text: `:${pad2(minute)}`
        });
      }
      return options;
    },
    weekDayOptions() {
      // Monday first, Sunday last (values stay 0 = Sunday .. 6 = Saturday)
      return [1, 2, 3, 4, 5, 6, 0].map(day => ({
        value: day,
        text: this.$i18n_t(`Common.${WEEKDAY_KEYS[day]}`)
      }));
    },
    monthDayOptions() {
      const options = [];
      for (let day = 1; day <= MAX_MONTH_DAY_OPTION; day++) {
        options.push({
          value: day,
          text: this.t('monthDayOption', {
            day: ordinalDay(day, this.language)
          })
        });
      }
      return options;
    },
    scheduleParams() {
      if (!this.schedule) {
        return {};
      }
      return {
        minute: pad2(this.schedule.minute || 0),
        time: this.timeString,
        weekDay: this.$i18n_t(`Common.${WEEKDAY_KEYS[this.schedule.weekDay] || 'Monday'}`),
        day: ordinalDay(this.schedule.monthDay || 1, this.language),
        timeZone: this.schedule.timeZone
      };
    },
    summaryText() {
      if (!this.schedule || !this.cron) {
        return '';
      }
      return this.t(`summary.${this.schedule.repeat}`, this.scheduleParams);
    },
    shortText() {
      if (!this.schedule || !this.cron) {
        return '';
      }
      return this.t(`short.${this.schedule.repeat}`, this.scheduleParams);
    },
    nextRunText() {
      if (!this.schedule || !this.cron) {
        return '';
      }
      const now = new Date();
      const next = nextRun(this.schedule, now);
      if (!next) {
        return '';
      }
      const inZone = momentTZ.tz(next, this.schedule.timeZone);
      const today = momentTZ.tz(now, this.schedule.timeZone);
      const time = inZone.format('HH:mm');
      // in a template every twin runs at its own slot within the hour, so only the window start is known
      const base = this.templateContext ? 'nextRunTemplate' : 'nextRun';
      if (inZone.isSame(today, 'day')) {
        return this.t(`${base}.today`, {
          time
        });
      }
      if (inZone.isSame(today.clone().add(1, 'day'), 'day')) {
        return this.t(`${base}.tomorrow`, {
          time
        });
      }
      return this.t(`${base}.date`, {
        date: inZone.clone().locale(this.language || 'en').format('D MMM'),
        time
      });
    },
    resultText() {
      if (this.schedule && this.cron) {
        return this.trigger.onStateUpdate ? this.t('resultBoth', {
          schedule: this.shortText
        }) : this.t('resultScheduleOnly', {
          schedule: this.shortText
        });
      }
      return this.trigger.onStateUpdate ? this.t('resultOnUpdateOnly') : this.t('resultNone');
    },
    descriptionHtml() {
      if (!this.templateContext) {
        return this.t('description');
      }
      const title = escapeHtml(this.templateTitle);
      if (this.linkedTwinCount >= 0) {
        return this.t('descriptionTemplate', {
          title,
          count: this.formatCount(this.linkedTwinCount)
        });
      }
      return this.t('descriptionTemplateNoCount', {
        title
      });
    },
    spreadInfoText() {
      if (this.linkedTwinCount >= 0) {
        return this.t('spreadInfo', {
          count: this.formatCount(this.linkedTwinCount)
        });
      }
      return this.t('spreadInfoNoCount');
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        const normalized = normalizeTrigger(newValue);
        if (!isEqual(normalized, this.trigger)) {
          this.trigger = normalized;
        }
      }
    },
    trigger: {
      deep: true,
      handler() {
        const out = cloneDeep(this.trigger);
        if (!isEqual(out, normalizeTrigger(this.value))) {
          this.$emit('input', out);
        }
        this.$emit('update');
      }
    }
  },
  methods: {
    t(key, params) {
      return this.$i18n_t(`${I18N_BASE}.${key}`, params);
    },
    formatCount(count) {
      try {
        return new Intl.NumberFormat(this.language || undefined).format(count);
      } catch (e) {
        return `${count}`;
      }
    },
    addSchedule() {
      this.trigger.schedule = defaultScheduleForDuration(this.duration, this.defaultTimeZone);
    },
    removeSchedule() {
      this.trigger.schedule = null;
    },
    setRepeat(repeat) {
      if (!this.schedule || !repeat || repeat === this.schedule.repeat) {
        return;
      }
      this.trigger.schedule = ensureScheduleFields({
        ...this.schedule,
        repeat
      });
    },
    setTime(value) {
      const parsed = parseTime(value);
      if (!parsed || !this.schedule) {
        return;
      }
      this.trigger.schedule = {
        ...this.schedule,
        hour: parsed.hour,
        minute: parsed.minute
      };
    }
  }
};